import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactElement } from "react";
import { FieldValues, useForm } from "react-hook-form";

import {
  UpdateApiKeyMutation,
  UpdateApiKeyMutationVariables,
} from "~frontend/__generated__/graphql";
import { submitErrorField } from "~frontend/components/forms";
import SubmitButton from "~frontend/components/forms/SubmitButton";
import {
  RhfErrorBanner,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { LIST_API_KEYS, UPDATE_API_KEY } from "~frontend/graphql/apiKeys";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import RhfApiKeyScopesField, {
  scopesToValue,
  valueToScopes,
} from "./RhfApiKeyScopesFields";
import FormSchema from "./formSchema";
import { ApiKey } from "./types";

type FormValues = FieldValues & {
  [submitErrorField]?: string;
  name: string;
  scopes: { [name: string]: boolean };
};

export function UpdateApiKeyDialog({
  apiKey,
  onClose,
  scopes,
}: {
  apiKey: ApiKey;
  onClose: () => void;
  scopes: string[];
}): ReactElement {
  const [updateApiKey] = useMutation<
    UpdateApiKeyMutation,
    UpdateApiKeyMutationVariables
  >(UPDATE_API_KEY, {
    refetchQueries: [LIST_API_KEYS],
    awaitRefetchQueries: true,
  });

  const snackbar = useSnackbar();
  const formatErrorMessage = useErrorMessageFormatter();

  const { control, handleSubmit, formState, setError, reset } =
    useForm<FormValues>({
      resolver: zodResolver(FormSchema),
      defaultValues: {
        name: apiKey.name,
        scopes: scopesToValue({
          allScopes: scopes,
          selectedScopes: apiKey.scopes,
        }),
      },
    });

  const handleUpdateApiKey = async (values: FormValues) => {
    const { errors } = await updateApiKey({
      variables: {
        input: {
          apiKeyId: apiKey.id,
          name: values.name,
          scopes: valueToScopes({
            allScopes: scopes,
            formValue: values.scopes,
          }),
        },
      },
    });

    if (errors) {
      setError("root", { message: formatErrorMessage(errors) });
    } else {
      snackbar.enqueueSnackbar({
        variant: "success",
        message: "Updated API Key",
      });
      onClose();
      reset();
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Update API Key</DialogTitle>
      <form onSubmit={handleSubmit(handleUpdateApiKey)}>
        <DialogContent>
          <RhfErrorBanner control={control} />
          <RhfTextField control={control} name="name" label="Name" fullWidth />

          <RhfApiKeyScopesField
            control={control}
            namePrefix="scopes"
            label="Scopes"
            scopes={scopes}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={formState.isSubmitting}
            onClick={onClose}
            color="secondary"
          >
            Cancel
          </Button>

          <SubmitButton
            isSubmitting={formState.isSubmitting}
            color="primary"
            variant="contained"
            submitText="Update"
            loadingText="Updating..."
          />
        </DialogActions>
      </form>
    </Dialog>
  );
}
